/**
 * PassengersMap.jsx
 * 
 * Similar to DriversMap.js but for Passengers:
 * 1. Shows all passengers on an interactive map
 * 2. Color-coded markers by status (online/offline/on_ride/waiting)
 * 3. InfoWindow popup on marker click
 * 4. Real-time status summary panel
 * 5. Socket connection for live updates
 * 6. Search & filter functionality
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { toast } from "react-toastify";
import { socket } from "../socket";
import AccessControl from "../components/AccessControl";

// 👤 Passenger marker images
import passenger_offline from "../assets/images/passenger_offline.png";
import passenger_online from "../assets/images/passenger_online.png";
import passenger_on_ride from "../assets/images/passenger_on_ride.png";
import passenger_waiting from "../assets/images/passenger_waiting.png";
import passenger_default from "../assets/images/passenger.png";
import { getAllPassengersByAdmin } from "../api/passengerApi";

const containerStyle = {
  width: "100%",
  height: "100vh",
};

// const center = {
//   lat: 11.0168,
//   lng: 76.9558,
// };

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// 🟢 Marker icon selector
const getMarkerIcon = (status) => {
  console.log(status, "<<< passenger status");
  switch (status) {
    case "online":
      return passenger_online;
    case "offline":
      return passenger_offline;
    case "on_ride":
      return passenger_on_ride;
    case "waiting":
      return passenger_waiting;
    default:
      return passenger_default;
  }
};

// ✅ Status display label
const getStatusLabel = (status) => {
  switch (status) {
    case "online":
      return "Online";
    case "offline":
      return "Offline";
    case "on_ride":
      return "On Ride";
    case "waiting":
      return "Waiting";
    default:
      return status;
  }
};

// ✅ Status color
const getStatusColor = (status) => {
  switch (status) {
    case "online":
      return "#22c55e";
    case "offline":
      return "#ef4444";
    case "on_ride":
      return "#f59e0b";
    case "waiting":
      return "#8b5cf6";
    default:
      return "#666";
  }
};

// ✅ Safe passenger mapper
const mapPassengerData = (p) => ({
  ...p,
  latitude: p.location?.coordinates?.[1] ?? p.latitude,
  longitude: p.location?.coordinates?.[0] ?? p.longitude,
});

const PassengersMap = () => {
  const [passengers, setPassengers] = useState([]);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(socket.connected);
  const socketRef = useRef(null);

const [mapCenter, setMapCenter] = useState(() => {
  // Lazy init — zone coords from logged-in admin's localStorage
  try {
    const _a = localStorage.getItem("admin");
    const _d = _a ? JSON.parse(_a) : null;
    const _c = _d?.zone?.location?.coordinates;
    if (_c?.lat && _c?.lng) return { lat: _c.lat, lng: _c.lng };
  } catch {}
  return { lat: 11.0168, lng: 76.9558 }; // fallback
});

  // 🔁 Fetch ALL passengers from API
  const getAllPassengers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllPassengersByAdmin();
      if (res.success) {
        const safeList = res.passengers
          .filter((p) => p.location?.coordinates)
          .map(mapPassengerData);
        setPassengers(safeList);
      } else {
        toast.error(res.error || "Failed to fetch passengers");
      }
    } catch (err) {
      console.error("getAllPassengers error:", err);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔌 Socket connection
  useEffect(() => {
    getAllPassengers();


// ✅ Browser geolocation — highest priority
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMapCenter({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        // Permission denied → zone coords already set in useState init, no action needed
        console.log("Geolocation denied — using zone coords");
      },
      { timeout: 5000 }
    );
  }

    socketRef.current = socket;

    // ✅ Connection status handlers
    const handleConnect = () => {
      console.log("🟢 Socket connected:", socket.id);
      setConnected(true);
      getAllPassengers(); // Refresh on reconnect
    };

    const handleDisconnect = (reason) => {
      console.log("🔴 Socket disconnected:", reason);
      setConnected(false);
    };

    // ✅ CRITICAL: Immediate status change handler
    const handlePassengerStatusChanged = (data) => {
      console.log("🔄 passenger-status-changed:", data);
      
      setPassengers((prev) =>
        prev.map((p) =>
          p.passengerId === data.passengerId
            ? { ...p, status: data.status }
            : p
        )
      );
      
      // Update selected passenger if it's the one that changed
      setSelectedPassenger((prev) => 
        prev?.passengerId === data.passengerId 
          ? { ...prev, status: data.status }
          : prev
      );
    };

    // ✅ Full passengers list handler
    const handlePassengersListAll = (payload) => {
      console.log("passengers-list-all received:", payload);

      const safeList = (payload.passengers || [])
        .filter((p) => p.location?.coordinates || (p.latitude && p.longitude))
        .map(mapPassengerData);

      setPassengers(safeList);
    };

    // ✅ Live location update handler
    const handlePassengerLocation = (data) => {
      console.log("passenger-location:", data);
      setPassengers((prev) =>
        prev.map((p) =>
          p.passengerId === data.passengerId
            ? {
                ...p,
                latitude: data.latitude,
                longitude: data.longitude,
                ...(data.status ? { status: data.status } : {}),
              }
            : p
        )
      );
    };

    // Register all event listeners
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("passenger-status-changed", handlePassengerStatusChanged);
    socket.on("passengers-list-all", handlePassengersListAll);
    socket.on("passenger-location", handlePassengerLocation);

    // Ride events that require full refetch
    const refetchEvents = [
      "ride-accepted",
      "ride-declined",
      "ride-cancelled",
      "trip_completed",
    ];
    refetchEvents.forEach((evt) => socket.on(evt, getAllPassengers));

    // ✅ Cleanup - DON'T disconnect, just remove listeners
    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("passenger-status-changed", handlePassengerStatusChanged);
      socket.off("passengers-list-all", handlePassengersListAll);
      socket.off("passenger-location", handlePassengerLocation);
      refetchEvents.forEach((evt) => socket.off(evt, getAllPassengers));
    };
  }, [getAllPassengers]);

  return (
    <div style={{ position: "relative" }}>
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN","HR", "NSM", "RSM", "ASM", "TSO"]}
        redirectTo="/404"
      />

      {/* 🔌 Connection status */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 999,
          background: connected ? "rgba(34, 197, 94, 0.9)" : "rgba(239, 68, 68, 0.9)",
          color: "#fff",
          padding: "6px 16px",
          borderRadius: 20,
          fontSize: 12,
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#fff",
            animation: connected ? "pulse 2s infinite" : "none",
          }}
        />
        {connected ? "Live" : "Disconnected"}
      </div>

      {/* 🔄 Loading overlay */}
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 999,
            background: "rgba(0,0,0,0.7)",
            color: "#fff",
            padding: "6px 16px",
            borderRadius: 20,
            fontSize: 13,
          }}
        >
          Loading passengers...
        </div>
      )}

      {/* 📊 Passenger status summary */}
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 999,
          background: "rgba(255,255,255,0.95)",
          borderRadius: 10,
          padding: "8px 14px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          fontSize: 12,
          lineHeight: "22px",
        }}
      >
        <div>
          🟢 Online:{" "}
          <b>{passengers.filter((p) => p.status === "online").length}</b>
        </div>
        <div>
          🔴 Offline:{" "}
          <b>{passengers.filter((p) => p.status === "offline").length}</b>
        </div>
        <div>
          🟡 Waiting:{" "}
          <b>{passengers.filter((p) => p.status === "waiting").length}</b>
        </div>
        <div>
          🛺 On Ride:{" "}
          <b>{passengers.filter((p) => p.status === "on_ride").length}</b>
        </div>
        <div>
          📍 Total: <b>{passengers.length}</b>
        </div>
      </div>

      <LoadScript googleMapsApiKey={GOOGLE_KEY}>
        <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={12}>
          {/* ✅ ALL passengers render */}
          {passengers.map((passenger) => (
            <Marker
              key={passenger.passengerId}
              position={{ lat: passenger.latitude, lng: passenger.longitude }}
              icon={{
                url: getMarkerIcon(passenger.status),
                scaledSize: new window.google.maps.Size(40, 40),
              }}
              onClick={() => setSelectedPassenger(passenger)}
            />
          ))}

          {/* 🪧 InfoWindow on click */}
          {selectedPassenger && (
            <InfoWindow
              position={{
                lat: selectedPassenger.latitude,
                lng: selectedPassenger.longitude,
              }}
              onCloseClick={() => setSelectedPassenger(null)}
            >
              <div style={{ textAlign: "center", width: 180 }}>
                {/* Avatar placeholder with gender icon */}
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    background: `${getStatusColor(selectedPassenger.status)}20`,
                    border: `3px solid ${getStatusColor(selectedPassenger.status)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                    fontSize: 24,
                  }}
                >
                  {selectedPassenger.gender === "Male" ? "👨" : "👩"}
                </div>
                
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: "bold" }}>
                  {selectedPassenger.name}
                </h4>
                <p style={{ margin: "2px 0", fontSize: 11, color: "#666" }}>
                  {selectedPassenger.passengerId}
                </p>
                <p
                  style={{
                    margin: "4px 0",
                    fontSize: 12,
                    fontWeight: "bold",
                    color: getStatusColor(selectedPassenger.status),
                  }}
                >
                  ● {getStatusLabel(selectedPassenger.status)}
                </p>
                <div style={{ 
                  textAlign: "left", 
                  fontSize: 11, 
                  color: "#555",
                  marginTop: 8,
                  padding: "8px",
                  background: "#f5f5f5",
                  borderRadius: 6,
                }}>
                  <p style={{ margin: "2px 0" }}>
                    📱 {selectedPassenger.phone}
                  </p>
                  {selectedPassenger.email && (
                    <p style={{ margin: "2px 0" }}>
                      ✉️ {selectedPassenger.email}
                    </p>
                  )}
                  <p style={{ margin: "2px 0" }}>
                    📍 {selectedPassenger.city}, {selectedPassenger.state}
                  </p>
                  <p style={{ margin: "2px 0" }}>
                    👤 {selectedPassenger.gender}
                  </p>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>

      {/* Add pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default PassengersMap;
